import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import type { CompanyAddress, CompanyAddressRequest } from '../types/address';
import type { CompanyLookup } from '../types/api';
import { CompanyAddressApi, type CompanyAddressApiContract } from './company-address.api';

/**
 * A service contract to interact with a company's addresses via the SparkLayer API.
 */
export interface CompanyAddressesApiContract {
  /** Every address in the company, across all of its sections. */
  list(): Promise<CompanyAddress[]>;
  create(payload: CompanyAddressRequest): Promise<CompanyAddress>;
  address(addressId: string): CompanyAddressApiContract;
}

/**
 * A service to interact with a company's addresses via the SparkLayer API.
 */
export class CompanyAddressesApi extends ApiService implements CompanyAddressesApiContract {
  constructor(
    api: ApiHandlerContract,
    private companyLookup: CompanyLookup,
  ) {
    super(api);
  }

  public list(): Promise<CompanyAddress[]> {
    return this.api.proxy({
      method: 'GET',
      path: '/v2/companies/{lookupBy}/{identifier}/addresses',
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  public create(payload: CompanyAddressRequest): Promise<CompanyAddress> {
    return this.api.proxy({
      method: 'POST',
      path: '/v2/companies/{lookupBy}/{identifier}/addresses',
      payload,
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  address(addressId: string): CompanyAddressApiContract {
    return new CompanyAddressApi(this.api, this.companyLookup, addressId);
  }
}
